import React, { useEffect, useState, useCallback } from 'react';
import {
  Table,
  Card,
  Typography,
  Tag,
  Space,
  Button,
  Tooltip,
  message,
  Spin,
  Empty,
  Badge,
} from 'antd';
import {
  SendOutlined,
  InboxOutlined,
  FileDoneOutlined,
  SafetyCertificateOutlined,
  ReloadOutlined,
  WarningOutlined,
  TeamOutlined,
  NotificationOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { ChecklistItem, ActionQueueType, ProjectTeamAssignment } from '../types';
import { useLookups } from '../context/LookupsContext';
import { useAuth } from '../context/AuthContext';
import ItemDetailDrawer from '../components/common/ItemDetailDrawer';

const { Title, Text } = Typography;

interface QueueConfig {
  title: string;
  description: string;
  icon: React.ReactNode;
  actionText: string;
  emptyText: string;
}

const queueConfigs: Record<ActionQueueType, QueueConfig> = {
  'to-request': {
    title: 'To Request',
    description: 'Items waiting for you to initiate a request',
    icon: <SendOutlined />,
    actionText: 'Request',
    emptyText: 'No items waiting to be requested',
  },
  'to-receive': {
    title: 'To Receive',
    description: 'Items requested from you that need acknowledgment',
    icon: <InboxOutlined />,
    actionText: 'Mark Received',
    emptyText: 'No items waiting to be received',
  },
  'to-resolve': {
    title: 'To Resolve',
    description: 'Items you need to complete and provide proof for',
    icon: <FileDoneOutlined />,
    actionText: 'Resolve',
    emptyText: 'No items waiting to be resolved',
  },
  'to-approve': {
    title: 'To Approve',
    description: 'Items waiting for your review and approval',
    icon: <SafetyCertificateOutlined />,
    actionText: 'Approve',
    emptyText: 'No items waiting for approval',
  },
  'consulting': {
    title: 'Consulting',
    description: 'Items where your team should be consulted for input',
    icon: <TeamOutlined />,
    actionText: 'View',
    emptyText: 'No items requiring your consultation',
  },
  'informed': {
    title: 'Informed',
    description: 'Items your team should be aware of',
    icon: <NotificationOutlined />,
    actionText: 'View',
    emptyText: 'No items to be informed about',
  },
};

const ActionQueue: React.FC = () => {
  const { queueType } = useParams<{ queueType: string }>();
  const navigate = useNavigate();
  const { getStatusName, getGateName } = useLookups();
  const { user } = useAuth();

  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<ChecklistItem | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [teamAssignments, setTeamAssignments] = useState<ProjectTeamAssignment[]>([]);

  // Validate queue type
  const validQueueType = queueType as ActionQueueType;
  const config = queueConfigs[validQueueType];

  const loadData = useCallback(async () => {
    if (!validQueueType || !config) return;

    setLoading(true);
    try {
      const data = await api.getActionQueueItems(validQueueType);
      setItems(data);
    } catch (error: any) {
      message.error('Failed to load items');
      console.error('Failed to load action queue:', error);
    } finally {
      setLoading(false);
    }
  }, [validQueueType, config]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleItemClick = async (item: ChecklistItem) => {
    setSelectedItem(item);
    setDrawerVisible(true);

    // Load team assignments for the project
    const projectId = item.checklist?.project?.id;
    if (projectId) {
      try {
        const assignments = await api.getProjectTeamAssignments(projectId);
        setTeamAssignments(assignments);
      } catch (error) {
        console.error('Failed to load team assignments:', error);
        setTeamAssignments([]);
      }
    } else {
      setTeamAssignments([]);
    }
  };

  const handleItemUpdate = async () => {
    await loadData();
    if (selectedItem) {
      try {
        const updated = await api.getChecklistItem(selectedItem.id);
        setSelectedItem(updated);
      } catch (error) {
        // Item might have moved to a different queue
        setDrawerVisible(false);
        setSelectedItem(null);
      }
    }
  };

  const getStatusTagColor = (status: string): string => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'resolved':
        return 'processing';
      case 'received':
        return 'cyan';
      case 'requested':
        return 'orange';
      case 'not_started':
      default:
        return 'default';
    }
  };

  const getPriorityTagColor = (priority: string): string => {
    switch (priority) {
      case 'high':
        return 'red';
      case 'med':
        return 'orange';
      case 'low':
        return 'green';
      default:
        return 'default';
    }
  };

  if (!config) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Empty description="Invalid queue type" />
        <Button type="primary" onClick={() => navigate('/')}>
          Go to Dashboard
        </Button>
      </div>
    );
  }

  const columns = [
    {
      title: 'Project',
      key: 'project',
      width: 150,
      render: (_: any, record: ChecklistItem) => (
        <span
          style={{ cursor: 'pointer', color: '#1890ff' }}
          onClick={(e) => {
            e.stopPropagation();
            const projectId = record.checklist?.project?.id;
            if (projectId) {
              navigate(`/projects/${projectId}`);
            }
          }}
        >
          {record.checklist?.project?.name || 'Unknown'}
        </span>
      ),
    },
    {
      title: 'Code',
      dataIndex: ['templateItem', 'itemCode'],
      key: 'itemCode',
      width: 80,
      render: (code: string) => <Text code>{code}</Text>,
    },
    {
      title: 'Item',
      dataIndex: ['templateItem', 'itemText'],
      key: 'itemText',
      ellipsis: true,
      render: (text: string, record: ChecklistItem) => (
        <Tooltip title={text}>
          <Space>
            {record.isOverdue && <WarningOutlined style={{ color: '#ff4d4f' }} />}
            <span style={{ cursor: 'pointer', color: '#1890ff' }}>
              {text}
            </span>
          </Space>
        </Tooltip>
      ),
    },
    {
      title: 'Gate',
      dataIndex: ['templateItem', 'gateNumber'],
      key: 'gateNumber',
      width: 70,
      render: (gate: number) => <Tag>G{gate}</Tag>,
    },
    {
      title: 'Type',
      dataIndex: ['templateItem', 'itemType'],
      key: 'itemType',
      width: 100,
      render: (type: string) => (
        <Tag color="cyan">{type?.replace('_', ' ').toUpperCase() || '-'}</Tag>
      ),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      render: (priority: string) =>
        priority ? (
          <Tag color={getPriorityTagColor(priority)}>{priority.toUpperCase()}</Tag>
        ) : (
          '-'
        ),
    },
  ];

  if (loading && !items.length) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Space align="center">
          <span style={{ fontSize: 24 }}>{config.icon}</span>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              {config.title}
              <Badge
                count={items.length}
                style={{ marginLeft: 12 }}
                showZero
              />
            </Title>
            <Text type="secondary">{config.description}</Text>
          </div>
        </Space>
      </div>

      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
            Refresh
          </Button>
        </div>

        {items.length === 0 ? (
          <Empty description={config.emptyText} />
        ) : (
          <Table
            columns={columns}
            dataSource={items}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `${total} items`,
            }}
            size="middle"
            onRow={(record) => ({
              onClick: () => handleItemClick(record),
              style: { cursor: 'pointer' },
            })}
          />
        )}
      </Card>

      <ItemDetailDrawer
        item={selectedItem}
        visible={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setSelectedItem(null);
          setTeamAssignments([]);
        }}
        onUpdate={handleItemUpdate}
        teamAssignments={teamAssignments}
      />
    </div>
  );
};

export default ActionQueue;
